import { configuredTransportOrigin } from '../../../shared/config/api-origin';
import type { MonitoringPort, WarningFilter } from '../consumer/contract';
import { parseAcknowledgement, parseDetail, parseSummary, parseWarnings } from './wire';
import { monitoringWriteSession } from './write-session';

export const MAX_READ_BYTES = 16 * 1024 * 1024;
// Historical forecast queries scan retained ledgers and share the monitor snapshot lock.
// Keep cancellation and a bounded deadline, but do not mislabel a valid slow read as unavailable.
export function monitoringDeadline(path: string, write: boolean): number {
  return !write && path.startsWith('/read-models/normal-monitoring/') ? 60_000 : 1500;
}

export class MonitoringReadError extends Error {
  constructor(readonly kind: 'HTTP' | 'WIRE' | 'TRANSPORT' | 'TIMEOUT') {
    super(`Local monitoring request failed: ${kind}`);
  }
}
export class WarningRequestError extends MonitoringReadError {
  constructor(
    readonly status: number,
    readonly code: string,
  ) {
    super('HTTP');
  }
}
export function parseWarningFailure(status: number, value: unknown): MonitoringReadError {
  if (!value || typeof value !== 'object' || Array.isArray(value))
    return new MonitoringReadError('WIRE');
  const data = value as Record<string, unknown>;
  if (
    Object.keys(data).sort().join(',') !== 'error,message' ||
    typeof data.error !== 'string' ||
    typeof data.message !== 'string'
  )
    return new MonitoringReadError('WIRE');
  return new WarningRequestError(status, data.error);
}
export function warningQuery(filter: WarningFilter): string {
  if (
    !Number.isSafeInteger(filter.limit) ||
    filter.limit < 1 ||
    filter.limit > 100 ||
    !Number.isSafeInteger(filter.offset) ||
    filter.offset < 0 ||
    filter.offset > 100000 ||
    !['', 'ACTIVE', 'INACTIVE'].includes(filter.state) ||
    filter.areaId.length > 128
  ) {
    throw new Error('Invalid warning filter');
  }
  for (const time of [filter.from, filter.to]) {
    if (time && (!/(?:Z|[+-]\d{2}:\d{2})$/.test(time) || !Number.isFinite(Date.parse(time)))) {
      throw new Error('Invalid time filter');
    }
  }
  if (filter.from && filter.to && Date.parse(filter.from) > Date.parse(filter.to)) {
    throw new Error('Invalid time range');
  }
  const params = new URLSearchParams();
  if (filter.areaId) params.set('areaId', filter.areaId);
  if (filter.state) params.set('state', filter.state);
  if (filter.from) params.set('from', filter.from);
  if (filter.to) params.set('to', filter.to);
  params.set('limit', String(filter.limit));
  params.set('offset', String(filter.offset));
  return params.toString();
}
export function createMonitoringRequest({
  baseOrigin,
  fetchImpl = fetch,
}: {
  readonly baseOrigin: string;
  readonly fetchImpl?: typeof fetch;
}) {
  const configured = configuredTransportOrigin(baseOrigin);
  const origin = new URL(configured ?? baseOrigin);
  if (
    !configured &&
    ((baseOrigin !== origin.origin && baseOrigin !== `${origin.origin}/`) ||
      origin.protocol !== 'http:' ||
      !['127.0.0.1', 'localhost', '[::1]'].includes(origin.hostname) ||
      origin.pathname !== '/' ||
      origin.search ||
      origin.hash ||
      origin.username ||
      origin.password)
  ) {
    throw new Error('Synthetic monitoring requires a loopback origin');
  }
  async function request<T>(
    path: string,
    parse: (value: unknown) => T,
    signal: AbortSignal,
    body?: unknown,
    failure?: (status: number, value: unknown) => MonitoringReadError,
  ): Promise<T> {
    const controller = new AbortController();
    const relayAbort = () => controller.abort();
    if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
    signal.addEventListener('abort', relayAbort, { once: true });
    let timedOut = false;
    const timeout = setTimeout(
      () => {
        timedOut = true;
        controller.abort();
      },
      monitoringDeadline(path, body !== undefined),
    );
    try {
      const options: RequestInit = {
        method: body ? 'POST' : 'GET',
        credentials: 'omit',
        redirect: 'error',
        cache: 'no-store',
        signal: controller.signal,
      };
      if (body) {
        options.headers = { 'Content-Type': 'application/json', 'X-Demo-Write': 'SYNTHETIC_ONLY' };
        const session = monitoringWriteSession();
        if (session !== null) options.headers['X-Rescue-Monitoring-Session'] = session;
        options.body = JSON.stringify(body);
      }
      const response = await fetchImpl(new URL(path, origin).href, options);
      if (response.status !== 200 && !failure) throw new MonitoringReadError('HTTP');
      if (
        (response.headers.get('content-type') ?? '').split(';')[0]?.trim().toLowerCase() !==
        'application/json'
      ) {
        throw new MonitoringReadError('WIRE');
      }
      const reader = response.body?.getReader();
      if (!reader) throw new MonitoringReadError('WIRE');
      const decoder = new TextDecoder('utf-8', { fatal: true });
      let bytes = 0;
      let raw = '';
      try {
        while (true) {
          const chunk = await reader.read();
          if (chunk.done) break;
          bytes += chunk.value.byteLength;
          if (bytes > MAX_READ_BYTES) {
            await reader.cancel();
            throw new MonitoringReadError('WIRE');
          }
          raw += decoder.decode(chunk.value, { stream: true });
        }
        raw += decoder.decode();
      } finally {
        reader.releaseLock();
      }
      if (response.status !== 200) {
        let value: unknown;
        try {
          value = JSON.parse(raw);
        } catch {
          throw new MonitoringReadError('WIRE');
        }
        throw failure?.(response.status, value) ?? new MonitoringReadError('HTTP');
      }
      try {
        return parse(JSON.parse(raw));
      } catch {
        throw new MonitoringReadError('WIRE');
      }
    } catch (error) {
      if (signal.aborted) throw error;
      if (timedOut) throw new MonitoringReadError('TIMEOUT');
      if (error instanceof MonitoringReadError) throw error;
      throw new MonitoringReadError('TRANSPORT');
    } finally {
      clearTimeout(timeout);
      signal.removeEventListener('abort', relayAbort);
    }
  }
  return request;
}
export function createMonitoringAdapter(options: {
  readonly baseOrigin: string;
  readonly fetchImpl?: typeof fetch;
}): MonitoringPort {
  const request = createMonitoringRequest(options);
  const readRoute = '/read-models/normal-monitoring';
  return {
    summary: (signal) => request(`${readRoute}/summary`, parseSummary, signal),
    warnings: (filter, signal) =>
      request(`${readRoute}/warnings?${warningQuery(filter)}`, parseWarnings, signal),
    detail: async (id, signal) => {
      const result = await request(
        `${readRoute}/warnings/${encodeURIComponent(id)}`,
        parseDetail,
        signal,
        undefined,
        parseWarningFailure,
      );
      if (result.warning.warningId !== id) throw new MonitoringReadError('WIRE');
      return result;
    },
    acknowledge: async (id, revision, signal) => {
      if (!Number.isSafeInteger(revision) || revision < 1)
        throw new Error('Invalid warning revision');
      const result = await request(
        `/normal-operations/warnings/${encodeURIComponent(id)}/acknowledgements`,
        parseAcknowledgement,
        signal,
        { revision, actor: 'local-demo-reader' },
        parseWarningFailure,
      );
      if (
        result.warningId !== id ||
        result.revision !== revision ||
        result.actor !== 'local-demo-reader'
      ) {
        throw new MonitoringReadError('WIRE');
      }
      return result;
    },
  };
}
